import { createTheme } from "@mui/material/styles";
import { SvgIcon } from "@mui/material";
import { tokens } from './designTokens';
// --------------making sure we follow 8px border radius rule throughout the app -----------
// Replace local constants with tokens (include alpha variants)
const {
  colors: {
    white,
    fontColour,
    fontColour90,
    fontColour85,
    fontColour15,
    fontColour08,
    headerFontColour,
    bodyBackgroundColor,
    titleBackgroundColor,
    panelTitleBackgroundColor,
    defaultGridHeaderBackground,
    darkgray,
    bodyBorderColor,
    bodyBorderColor50,
    defaultGridBorderColor,
    defaultGridRowColor,
    defaultGridRowAltColor,
    defaultGridHoverColor,
    defaultGridColor,
    defaultGridSelectionColor,
    verylightgray,
    gray,
  },
  palette: {
    primary: primaryColor,
    primaryA90,
    primaryA85,
    primaryA30,
    primaryA25,
    primaryA10,
    primaryA08,
    primaryA04,
    secondary: secondaryColor,
    secondaryA10,
    danger: dangerColor,
  },
  typography: {
    fontFamilyDefault,
  }
} = tokens;

function UncheckedIcon(props) {
  return (
    <SvgIcon viewBox="0 0 24 24" {...props}>
      <rect
        x="3"
        y="3"
        width="18"
        height="18"
        fill="transparent"
        stroke={darkgray}
        strokeWidth="2"
        opacity="0.6"
      />
    </SvgIcon>
  );
}

function CheckedIcon(props) {
  return (
    <SvgIcon viewBox="0 0 24 24" {...props}>
      <rect
        x="3"
        y="3"
        width="18"
        height="18"
        fill="transparent"
        stroke={primaryColor}
        strokeWidth="2"
      />
      <path
        d="M7 12.5L10.5 16l6.5-6.5"
        stroke={primaryColor}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </SvgIcon>
  );
}

export const theme = createTheme({
  shape: {
    borderRadius: 8,
  },
  palette: {
    mode: 'light',
    background: {
      default: bodyBackgroundColor,
      paper: white,
    },
    text: {
      primary: fontColour,
      secondary: darkgray,
    },
    primary: {
      main: primaryColor,
      light: primaryA90,
      dark: primaryColor,
    },
    secondary: {
      main: secondaryColor,
    },
    error: {
      main: dangerColor
    }
  },
  typography: {
    fontFamily: fontFamilyDefault,
    allVariants: {
      color: fontColour,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: bodyBackgroundColor,
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-track': {
            background: verylightgray,
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: tokens.colors.scrollbarThumb,
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(51, 51, 51, 0.3)',
            },
          },
          scrollbarWidth: 'thin',
          scrollbarColor: `${tokens.colors.scrollbarThumb} ${tokens.colors.scrollbarTrack}`,
          '& *': {
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(51, 51, 51, 0.2) rgba(0, 0, 0, 0.05)',
          }
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: `${primaryColor} !important`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: white,
          borderRadius: '8px',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          display: "none",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: white,
            color: primaryColor,
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
            fontWeight: 'bold'
          },
          "&.subtab.Mui-selected": {
            backgroundColor: titleBackgroundColor,
            color: fontColour,
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
            fontWeight: 700
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          padding: '14px !important',
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        icon: <UncheckedIcon />,
        checkedIcon: <CheckedIcon />,
      },
      styleOverrides: {
        root: {
          padding: '8px',
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '&.Mui-checked': {
            '& .MuiSvgIcon-root': {
              color: 'transparent',
            },
          },
          '& .MuiSvgIcon-root': {
            color: 'transparent',
          },
          '&.Mui-disabled': {
            opacity: 0.45,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: fontFamilyDefault,
          fontWeight: 700,
          fontSize: '16px',
          lineHeight: '18px',
          letterSpacing: '0%',
          textAlign: 'center',
          borderRadius: '8px',
        },
        contained: {
          '&.MuiButton-containedPrimary': {
            backgroundColor: primaryColor,
            color: white,
            '&:hover': {
              backgroundColor: primaryA85,
            },
          },
          '&.MuiButton-containedSecondary': {
            backgroundColor: white,
            color: fontColour,
            border: `1px solid ${bodyBorderColor}`,
            '&:hover': {
              backgroundColor: verylightgray,
            },
          },
          '&.MuiButton-containedInherit': {
            backgroundColor: defaultGridHoverColor,
            color: fontColour,
            fontWeight: 400,
            border: `0.5px solid ${bodyBorderColor50}`, // was `${bodyBorderColor}80`
            '&:hover': {
              backgroundColor: titleBackgroundColor,
            },
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        grouped: {
          borderRadius: '8px',
          border: `1px solid ${bodyBorderColor}`,
          margin: 0,
          "&:not(:first-of-type)": {
            borderLeft: `1px solid ${bodyBorderColor}`,
            marginLeft: 0,
          },
          "&:first-of-type": {
            borderTopLeftRadius: '8px',
            borderBottomLeftRadius: '8px',
          },
          "&:last-of-type": {
            borderTopRightRadius: '8px',
            borderBottomRightRadius: '8px',
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          color: fontColour,
          backgroundColor: "transparent",
          paddingLeft: 12,
          paddingRight: 12,
          height: 28,
          border: `1px solid ${defaultGridBorderColor}`,
          borderRadius: '8px',
          "&:hover": { backgroundColor: primaryA04 }, // was primaryMuted / hex 0A
        },
        rootPrimary: {},
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: fontColour,
          '&:hover': {
            backgroundColor: primaryA10, // was `${primaryColor}1A`
          },
          '&.Mui-selected': {
            backgroundColor: primaryColor,
            color: white,
            '&:hover': {
              backgroundColor: primaryA85, // was `${primaryColor}D9`
            },
          },
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          color: fontColour,
        },
        label: {
          color: fontColour,
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          backgroundColor: white,
          color: fontColour,
        },
      },
    },
    MuiPickersPopper: {
      styleOverrides: {
        paper: {
          backgroundColor: white,
          color: fontColour,
          border: `1px solid ${bodyBorderColor}`,
          borderRadius: '8px',
        },
      },
    },
    MuiClock: {
      styleOverrides: {
        root: {
          backgroundColor: white,
        },
        clock: {
          backgroundColor: bodyBackgroundColor,
        },
      },
    },
    MuiClockNumber: {
      styleOverrides: {
        root: {
          color: fontColour,
          '&.Mui-selected': {
            backgroundColor: primaryColor,
            color: white,
          },
        },
      },
    },
    MuiClockPointer: {
      styleOverrides: {
        root: {
          backgroundColor: primaryColor,
        },
        thumb: {
          backgroundColor: primaryColor,
          borderColor: primaryColor,
        },
      },
    },
    MuiPickersToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: white,
          color: fontColour90, // was `${fontColour}E6`
        },
      },
    },
    MuiDigitalClock: {
      styleOverrides: {
        root: {
          backgroundColor: white,
          '& .MuiButtonBase-root': {
            color: fontColour85, // was `${fontColour}D9`
            '&.Mui-selected': {
              backgroundColor: primaryA25, // was `${primaryColor}40`
              color: primaryColor,
            },
            '&:hover': {
              backgroundColor: primaryA08, // was `${primaryColor}14`
            },
          },
        },
      },
    },
    MuiMultiSectionDigitalClock: {
      styleOverrides: {
        root: {
          backgroundColor: white,
          '& .MuiMultiSectionDigitalClockSection-root': {
            borderColor: fontColour15, // was `${fontColour}26`
          },
          '& .MuiButtonBase-root': {
            color: fontColour85,
            '&.Mui-selected': {
              backgroundColor: primaryA25,
              color: primaryColor,
            },
            '&:hover': {
              backgroundColor: primaryA08,
            },
          },
        },
      },
    },
    MuiPickersActionBar: {
      styleOverrides: {
        root: {
          backgroundColor: white,
          '& .MuiButton-root': {
            textTransform: 'none',
            fontWeight: 600,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: `${white} !important`,
          color: `${fontColour} !important`,
          border: `1px solid ${defaultGridBorderColor}`,
          borderRadius: '8px',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: `${white} !important`,
          color: `${fontColour} !important`,
          fontWeight: 700,
          borderBottom: `1px solid ${defaultGridBorderColor}`,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          backgroundColor: `${white} !important`,
          color: `${fontColour} !important`,
          paddingTop: 16,
          '& .MuiFormControl-root': { width: '100%' },
          '& .MuiInputLabel-root': {
            color: `${darkgray} !important`,
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: `${fontColour} !important`,
          },
          '& .MuiOutlinedInput-root': {
            backgroundColor: `${white} !important`,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: `${defaultGridBorderColor} !important`,
          },
          '& .MuiInputBase-input::placeholder': {
            color: gray,
            opacity: 1,
          },
          '& .MuiSelect-select': {
            backgroundColor: `${white} !important`,
            color: `${fontColour} !important`,
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: `${white} !important`,
          borderTop: `1px solid ${defaultGridBorderColor}`,
          padding: '12px 16px',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: `${white} !important`,
          color: `${fontColour} !important`,
          border: `1px solid ${defaultGridBorderColor}`,
          borderRadius: '8px',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: { backgroundColor: `${white} !important` }
      }
    },
    MuiTable: {
      styleOverrides: {
        root: { backgroundColor: `${white} !important`, color: fontColour }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: { backgroundColor: `${defaultGridHeaderBackground} !important` }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: `${fontColour} !important`,
          borderBottom: `1px solid ${defaultGridBorderColor}`,
        },
        head: {
          color: `${headerFontColour} !important`,
          fontWeight: 600,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { backgroundColor: `${white} !important` }
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          backgroundColor: `${white} !important`,
          borderColor: defaultGridBorderColor,
          color: `${fontColour} !important`,
          borderRadius: '8px',
        },
        columnHeaders: {
          backgroundColor: `${defaultGridHeaderBackground} !important`,
          color: `${headerFontColour} !important`,
          borderBottom: `1px solid ${defaultGridBorderColor}`
        },
        cell: {
          color: `${fontColour} !important`,
          borderColor: defaultGridBorderColor
        },
        row: { backgroundColor: `${white} !important` },
      },
    },
  },
});